import React from 'react';
import PropTypes from 'prop-types';
import { MdCheckCircle, MdEdit, MdOutlineAccountCircle } from 'react-icons/md';
import { CustomFormInput, SpinnerLoader } from '../../../atoms';
import { useQuery } from '../../../../hooks';
import classes from './CfStudentIdentifier.module.scss';

const CfStudentIdentifier = (props) => {
    const {
        formBlockData,
        formik,
        studentDetails,
        isStudentDetailsLoading,
        studentDetailsApiCount,
        isStudentVerified,
        handleVerifyStudentClick,
        handleStudentIdEdit,
        disableSidField,
        // New props for primary identifier
        primaryIdentifier,
        isPrimaryIdentifierFlow,
        isPrimaryOtpVerified
    } = props;

    const query = useQuery();

    const studentInput = formBlockData?.fields?.flat()?.find((field) => field?.input_id === 'stud_bd_student_uuid');
    if (!studentInput) return null;

    // Create a modified input object for primary identifier flow
    const getStudentInputWithPrimaryLabel = () => {
        if (!isPrimaryIdentifierFlow) return studentInput;
        
        const modifiedInput = { ...studentInput };
        if (primaryIdentifier === 'customer_mobile') {
            modifiedInput.label = 'Customer Mobile';
            modifiedInput.placeholder = 'Enter Customer Mobile';
        } else if (primaryIdentifier === 'customer_email') {
            modifiedInput.label = 'Customer Email';
            modifiedInput.placeholder = 'Enter Customer Email';
        }
        
        return modifiedInput;
    };

    const currentStudentInput = studentInput;

    const isStdInputDisabled = studentDetailsApiCount > 0 
        || (query.get('uid') && formik?.values?.stud_bd_student_uuid) 
        || (disableSidField && query.get('sid') && formik?.values?.stud_bd_student_uuid);
    const isStdIdEditVisible = !(query.get('uid') && formik?.values?.stud_bd_student_uuid)
        || !(disableSidField && query.get('sid') && formik?.values?.stud_bd_student_uuid);
    const blockUnverifiedStudent = formBlockData?.form_config?.block_unverified_student ?? false;

    const renderVerifyButton = () => {
        return (
            <button
                type="button"
                onClick={handleVerifyStudentClick}
                title={`Verify ${currentStudentInput?.label || 'student ID'}`}
                className={classes.VerifyStudentIDButton}
                disabled={isStudentDetailsLoading}
            >
                {`Click here to verify ${currentStudentInput?.label || 'student ID'}`}
                {isStudentDetailsLoading ? (
                    <span>
                        {' '}
                        <SpinnerLoader size="tiny" />{' '}
                    </span>
                ) : null}
            </button>
        );
    };

    const renderStudentName = () => {
        if (!studentDetails?.stud_bd?.first_name) return null;
        return (
            <>
                <span className={classes.StudentName}>
                    <span className={classes.Label}>
                        <MdOutlineAccountCircle fontSize="1.25rem" /> Student Name: &nbsp;
                    </span>
                    {`${studentDetails?.stud_bd?.first_name || ''} ${studentDetails?.stud_bd?.middle_name || ''} ${studentDetails?.stud_bd?.last_name || ''}`}
                </span>
                <br />
            </>
        );
    };

    const renderStdVerifText = () => {
        // For primary identifier flow, also check if OTP is verified when required
        if (isPrimaryIdentifierFlow) {
            const enableMobileOtpVerification = formBlockData?.form_config?.enable_otp_verification ?? false;
            const enableEmailOtpVerification = formBlockData?.form_config?.enable_email_otp_verification ?? false;
            const isPrimaryMobile = primaryIdentifier === 'customer_mobile';
            const isPrimaryEmail = primaryIdentifier === 'customer_email';
            
            // Check if OTP is required for primary identifier
            const primaryOtpRequired = (isPrimaryMobile && enableMobileOtpVerification) || 
                                       (isPrimaryEmail && enableEmailOtpVerification);
            
            if (primaryOtpRequired && !isPrimaryOtpVerified) {
                // OTP is required but not verified yet, don't show verification text
                return null;
            }
        }
        
        if (blockUnverifiedStudent && studentDetails) {
            return (
                <>
                    {' '}
                    <span className={classes.Icon}>
                        <MdCheckCircle fontSize="1.25rem" />
                    </span>
                    {currentStudentInput?.label || 'student ID'} verified successfully!
                </>
            );
        } else if (!blockUnverifiedStudent) {
            return (
                <>
                    {' '}
                    <span className={classes.Icon}>
                        <MdCheckCircle fontSize="1.25rem" />
                    </span>
                    {currentStudentInput?.label || 'student ID'} verified successfully!
                </>
            );
        } else {
            return null;
        }
    };

    const renderEditButton = () => {
        return (
            <button
                type="button"
                onClick={handleStudentIdEdit}
                title={`Change ${currentStudentInput?.label || 'student ID'}`}
                className={classes.StudentIdChangeAction}
            >
                &nbsp;{' '}
                <span className={classes.Icon}>
                    <MdEdit />
                </span>{' '}
                {`Click here to change ${currentStudentInput?.label || 'Student ID'}`}
            </button>
        );
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className={classes.StudentInputWrap}>
                    <CustomFormInput
                        inputData={currentStudentInput || null}
                        formik={formik}
                        key="studentInput"
                        inputClassName={classes.StudentInput}
                        isDisabled={isStdInputDisabled}
                        {...(isPrimaryIdentifierFlow && primaryIdentifier === 'customer_mobile' && { maxLength: 10 })}
                    />
                    {formik?.values?.stud_bd_student_uuid && (
                        <>
                            {!isStudentVerified && studentDetailsApiCount === 0
                                ? renderVerifyButton()
                                : studentDetailsApiCount > 0 && (
                                      <p className={classes.StudentVerifiedText}>
                                          {renderStudentName()}
                                          <span className={classes.Text}>
                                              {renderStdVerifText()}
                                              {isStdIdEditVisible && renderEditButton()}
                                          </span>
                                      </p>
                                  )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

CfStudentIdentifier.propTypes = {
    formBlockData: PropTypes.object,
    formik: PropTypes.object,
    studentDetails: PropTypes.object,
    isStudentDetailsLoading: PropTypes.bool,
    studentDetailsApiCount: PropTypes.number,
    isStudentVerified: PropTypes.bool,
    handleVerifyStudentClick: PropTypes.func,
    handleStudentIdEdit: PropTypes.func,
    disableSidField: PropTypes.bool,
    primaryIdentifier: PropTypes.string,
    isPrimaryIdentifierFlow: PropTypes.bool,
    isPrimaryOtpVerified: PropTypes.bool,
};

export default CfStudentIdentifier;
